import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { BUSINESS_NAME_LEGAL as BUSINESS_NAME, PHONE, EMAIL, ORG_SCHEMA } from "@/lib/constants";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://truamericanplumbing.com/" },
    { "@type": "ListItem", position: 2, name: "Terms of Service", item: "https://truamericanplumbing.com/terms-of-service" },
  ],
};

const TermsOfService = () => {
  return (
    <>
      <SEO
        title="Terms of Service"
        description={`Terms of Service for ${BUSINESS_NAME}. Includes SMS program terms, opt-out instructions, carrier disclaimers, and website usage terms.`}
        canonicalUrl="/terms-of-service/"
        schema={[ORG_SCHEMA, breadcrumbSchema]}
      />

      <section className="py-16 md:py-20">
        <div className="container max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Terms of Service
          </h1>
          <p className="text-muted-foreground mb-10">
            Last Updated: February 24, 2026
          </p>

          <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">
            {/* Introduction */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">1. Introduction</h2>
              <p>
                Welcome to the website of {BUSINESS_NAME} ("we," "us," or "our"). By
                accessing or using our website and services, you agree to be bound by these
                Terms of Service. If you do not agree, please do not use our website or services.
              </p>
            </div>

            {/* SMS Program Terms */}
            <div className="bg-muted border border-border rounded-xl p-6">
              <h2 className="text-2xl font-bold text-foreground mb-3">
                2. SMS Messaging Program Terms
              </h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Program Name</h3>
              <p>{BUSINESS_NAME} SMS Notifications</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Program Description</h3>
              <p>
                By opting in to our SMS program, you consent to receive text messages from
                {" "}{BUSINESS_NAME} related to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Appointment reminders and confirmations</li>
                <li>Service updates and scheduling notifications</li>
                <li>Emergency service alerts</li>
                <li>Follow-up communications regarding your service requests</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Message Frequency</h3>
              <p>
                Message frequency varies based on your service interactions and appointments.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Message and Data Rates</h3>
              <p>
                <strong>Message and data rates may apply.</strong> Please contact your wireless
                carrier for details about your messaging plan.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Opt-Out (STOP)</h3>
              <p>
                You can cancel the SMS service at any time. Simply text <strong>STOP</strong> to
                the shortcode or phone number from which you received messages. Upon sending
                STOP, we will confirm your unsubscribe status via SMS. After opting out, you
                will no longer receive SMS messages from us unless you re-enroll.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Rejoining the Program</h3>
              <p>
                To rejoin, sign up as you did initially (e.g., through our website contact
                form), and we will resume sending SMS messages to you.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Help (HELP)</h3>
              <p>
                If you experience issues with the messaging program, reply with the keyword{" "}
                <strong>HELP</strong> for more assistance, or reach out directly to{" "}
                <a href={`mailto:${EMAIL}`} className="text-primary hover:text-accent underline">
                  {EMAIL}
                </a>{" "}
                or{" "}
                <a href={`tel:${PHONE.replace(/-/g, "")}`} className="text-primary hover:text-accent underline">
                  {PHONE}
                </a>.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Carrier Liability</h3>
              <p>
                <strong>Carriers are not liable for delayed or undelivered messages.</strong>{" "}
                Delivery of messages is subject to effective transmission by your wireless
                carrier. {BUSINESS_NAME} is not responsible for messages that are not delivered
                due to carrier issues or device incompatibility.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">Privacy</h3>
              <p>
                Your privacy is important to us. Please review our{" "}
                <Link to="/privacy-policy/" className="text-primary hover:text-accent underline">
                  Privacy Policy
                </Link>{" "}
                for information on how we handle your personal data, including mobile
                information collected through our SMS program.
              </p>
            </div>

            {/* Services */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">3. Services</h2>
              <p>
                {BUSINESS_NAME} provides plumbing and water purification services to
                residential and commercial customers in Tolleson and the West Valley area of
                Arizona. Our services include but are not limited to emergency plumbing, drain
                cleaning, slab leak detection, water heater repair and installation, water
                softener systems, and reverse osmosis installations.
              </p>
            </div>

            {/* Website Use */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">4. Website Use</h2>
              <p>You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Use the website in any way that violates applicable laws or regulations</li>
                <li>Attempt to interfere with or disrupt the website's functionality</li>
                <li>Transmit any malicious code, viruses, or harmful content</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">5. Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, images, and
                software, is the property of {BUSINESS_NAME} or its content suppliers and is
                protected by United States and international copyright laws. You may not
                reproduce, distribute, or create derivative works from any content without
                our express written permission.
              </p>
            </div>

            {/* Disclaimer of Warranties */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">6. Disclaimer of Warranties</h2>
              <p>
                Our website and services are provided "as is" and "as available" without
                warranties of any kind, either express or implied. We do not warrant that the
                website will be uninterrupted, error-free, or free of viruses or other
                harmful components.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">7. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, {BUSINESS_NAME} shall not be liable
                for any indirect, incidental, special, consequential, or punitive damages
                arising out of or related to your use of or inability to use our website or
                services.
              </p>
            </div>

            {/* Indemnification */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">8. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless {BUSINESS_NAME}, its owners,
                employees, and agents from and against any claims, damages, losses, or
                expenses arising out of your use of our website or violation of these Terms.
              </p>
            </div>

            {/* Governing Law */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">9. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of
                the State of Arizona, without regard to its conflict of law provisions.
              </p>
            </div>

            {/* Changes */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">10. Changes to These Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be
                posted on this page with an updated "Last Updated" date. Your continued use
                of our website or services after changes constitutes your acceptance of the
                updated Terms.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-3">11. Contact Us</h2>
              <p>If you have questions about these Terms, please contact us:</p>
              <ul className="list-none mt-3 space-y-1">
                <li><strong>{BUSINESS_NAME}</strong></li>
                <li>Tolleson, AZ — Serving the West Valley</li>
                <li>Phone: <a href={`tel:${PHONE.replace(/-/g, "")}`} className="text-primary hover:text-accent underline">{PHONE}</a></li>
                <li>Email: <a href={`mailto:${EMAIL}`} className="text-primary hover:text-accent underline">{EMAIL}</a></li>
              </ul>
            </div>

            {/* Compliance */}
            <div className="bg-muted border border-border rounded-xl p-6">
              <h2 className="text-xl font-bold text-foreground mb-3">Industry Compliance</h2>
              <p>
                {BUSINESS_NAME} is committed to compliance with all applicable regulations
                governing SMS communications, including the Telephone Consumer Protection Act
                (TCPA) and Cellular Telecommunications Industry Association (CTIA) guidelines.
                Our SMS program is designed to meet the requirements of A2P 10DLC registration
                and carrier compliance standards.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;
